import type { Config } from "@puckeditor/core";

const DUMMY = "refresh";

import type { ReactNode } from "react";

import type { ComponentRefProps } from "../components/component-ref/component-ref";
import type { NavigationProps } from "../components/navigation/navigation";
import type { FooterProps } from "../components/footer/footer";
import { ComponentRefConfig } from "../components/component-ref/component-ref.config";
import { Navigation } from "../components/navigation/navigation";
import { Footer } from "../components/footer/footer";
import { MobileStickyCall } from "../components/mobile-sticky-call/mobile-sticky-call";
import { PageEditorComponentRegistry } from './components.registry';
import type { JobsitePageComponentProps } from './components.registry';

/**
 * Root props interface for theme and global settings
 * Navigation and footer data comes from SiteComponents but is rendered here for the editor preview
 */
interface RootProps {
  title?: string;
  navigation?: Omit<NavigationProps, "id" | "className">;
  footer?: Omit<FooterProps, "id" | "className">;
  children: ReactNode;
}

/**
 * Puck configuration for jobsite components
 *
 * Navigation and Footer are NOT included here - they are managed via
 * SiteComponents and rendered separately in the site build process.
 */
const puckConfig: Config<
  JobsitePageComponentProps & {
    ComponentRef: ComponentRefProps;
  },
  RootProps
> = {
  /**
   * How components group in a picker. Puck's own field, so the grouping lives
   * with the components rather than in whichever UI happens to list them —
   * anything unlisted falls through to "Other" rather than disappearing.
   */
  categories: {
    sections: {
      title: "Sections",
      components: [
        "Hero",
        "Services",
        "Gallery",
        "BeforeAfter",
        "Reviews",
        "ContentBlock",
        "ProcessSteps",
        "BenefitsList",
        "FAQAccordion",
        "ServiceAreas",
        "PricingInfo",
        "BrandMarquee",
        "Video",
        "CTA",
        "EmergencyBand",
        "Breadcrumb",
      ],
    },
    capture: {
      components: ["ContactForm", "FormBlock", "BookingWidget"],
      title: "Capture",
    },
    content: {
      components: ["HeadingBlock", "TextBlock", "BadgeBlock", "ImageBlock", "ButtonBlock"],
      title: "Content",
    },
    layout: {
      components: ["GroupBlock", "SectionBlock", "SpacerBlock", "DividerBlock"],
      title: "Layout",
    },
    // Page-kind components: rendered on their dedicated page, never offered in
    // A picker — the /links page's locked permissions are what add/remove them.
    page: {
      components: ["LinksPage"],
      title: "Page",
      visible: false,
    },
  },
  components: {
    ...PageEditorComponentRegistry,
    ComponentRef: ComponentRefConfig,
  },
  root: {
    defaultProps: {},
    fields: {},
    render: ({ children }) => {
      // Navigation, footer, and theme come from window global (set by editor or build process)
      // This ensures they're available even if not in Puck data props
      // In editor context, Puck runs in an iframe so we check parent window first
      const getConfig = () => {
          if (typeof window === "undefined") {
            return {};
          }
          // Try parent window first (editor iframe context)
          try {
            if (
              window.parent &&
              window.parent !== window &&
              (window.parent as any).__SERVICEPRO_CONFIG__
            ) {
              return (window.parent as any).__SERVICEPRO_CONFIG__;
            }
          } catch {
            // Cross-origin access denied, fall back to current window
          }
          // Fall back to current window (deployed site context)
          return (window as any).__SERVICEPRO_CONFIG__ || {};
        },
        config = getConfig(),
        navigation = config?.navigation,
        footer = config?.footer,
        mobileStickyCall = config?.mobileStickyCall;
      return (
        <div className="jobsite-container">
          {/* Navigation from SiteComponent data */}
          {navigation && <Navigation {...navigation} />}

          {children}

          {/* Footer from SiteComponent data */}
          {footer && <Footer {...footer} />}

          {/* Mobile Sticky Call from SiteComponent data. The nav's "Show Text
              Us" setting is the single source of truth, so the sticky bar
              follows it (falling back to its own value). */}
          {mobileStickyCall && (
            <MobileStickyCall
              {...mobileStickyCall}
              showTextUs={navigation?.showTextUs ?? mobileStickyCall.showTextUs}
            />
          )}
        </div>
      );
    },
  },
};

export type { RootProps };
export { DUMMY, puckConfig };
